const yup = require("yup");

// Payment email prefix validation (SendGrid-compatible)
// Allowed: a-z, 0-9, and these special chars: ! # $ % & ' * + - / = ? ^ _ ` { | } ~ .
// 1. Length: 3-64 chars
// 2. Only allowed chars
// 3. Cannot start or end with dot (.)
// 4. Cannot have two consecutive dots (..)
const paymentEmailRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]{3,64}$/;

// What every card issuer prints on a physical card, as CARD_NAME_MAX_LENGTH and
// CARD_NAME_PATTERN in the shared constants hold it.
const BUSINESS_CARD_NAME_MAX_LENGTH = 23;
const businessCardNameRegex = /^[A-Za-z0-9 &'./-]+$/;

// As on the server, the name the form loaded is checked only once it changes.
const getBusinessCardNameRules = (loadedCardName) =>
  yup
    .string()
    .test(
      "printable-length",
      "BusinessCardNameTooLong",
      (value) =>
        !value ||
        value === loadedCardName ||
        value.length <= BUSINESS_CARD_NAME_MAX_LENGTH,
    )
    .test(
      "printable-characters",
      "BusinessCardNameCharacters",
      (value) =>
        !value || value === loadedCardName || businessCardNameRegex.test(value),
    );

const paymentEmailRules = yup
  .string()
  // Options go in the second argument: yup's matches takes (regex, options),
  // so a third argument is read by nothing and the rule keeps rejecting "".
  .matches(paymentEmailRegex, {
    message:
      "Invalid payment email prefix. Only a-z, 0-9, and ! # $ % & ' * + - / = ? ^ _ ` { | } ~ . are allowed, 3-64 chars.",
    excludeEmptyString: true,
  })
  .test(
    "no-leading-or-trailing-dot",
    "Payment email cannot start or end with a dot.",
    (val) => !val || (!/^\./.test(val) && !/\.$/.test(val)),
  )
  .test(
    "no-consecutive-dots",
    "Payment email cannot contain consecutive dots.",
    (val) => !val || !/\.\./.test(val),
  );

/**
 * `requirePaymentEmail` follows the payments entitlement. The profile tab hides
 * that field without it, and a required field nobody can see never passes, which
 * leaves Save disabled on every other field with nothing on screen to explain it.
 * @param {{ requirePaymentEmail?: boolean, loadedCardName?: string }} [options]
 */
const buildCompanySettings = ({
  requirePaymentEmail = true,
  loadedCardName,
} = {}) =>
  yup.object().shape({
    businessLegalName: yup.string().required("Business Legal Name is required"),
    description: yup.string().required("Description is required"),
    businessDBA: yup.string().required("Doing Business As (DBA) is required"),
    businessCardName: getBusinessCardNameRules(loadedCardName).required(
      "Business Card Name is required",
    ),
    // No entitlement means no rendered field, so every rule on it is one the
    // reader can neither satisfy nor see. A legacy value that fails the prefix
    // rules would block Save exactly as the missing value did.
    paymentEmail: requirePaymentEmail
      ? paymentEmailRules.required("Payment Email is required")
      : yup.string(),
    businessEmail: yup.string(), // Not required
    phoneNumber: yup.string(), // Not required
    websiteURL: yup.string(), // Not required
  });

export {
  BUSINESS_CARD_NAME_MAX_LENGTH,
  getBusinessCardNameRules,
  buildCompanySettings,
};
export default buildCompanySettings();
